import {useClientLocator} from "../../../client/ApiClientLocator";
import {
    SubmissionDeliveryDto,
    SubmissionDeliveryResultListDto,
    SubmissionDeliveryStateType,
    SubmissionDto
} from "../../../client/submission/SubmissionApiClient";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "@dain/mui-components"
import {useCallback, useEffect, useRef, useState} from "react";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import Button from "../../../common/button/Button";
import Skeleton from "../../../common/skeleton/Skeleton";
import SubmissionDeliveryGrid from "./SubmissionDeliveryGrid";
import ConfirmationDialog from "../../../common/dialog/ConfirmationDialog";
import {Stack} from "@mui/material";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {useUserRoles} from "../../user/UserContext";
import UserRoleResolver from "../../user/UserRoleResolver";

class DeliveryTimeoutError extends Error {}

const AUTO_REFRESH_INTERVAL_MS = 10_000;

function isDeliveryInProgressState(stateType?: SubmissionDeliveryStateType): boolean {
    return stateType === SubmissionDeliveryStateType.DELIVERING
        || stateType === SubmissionDeliveryStateType.INITIALIZING_DELIVERY;
}

function hasInProgressDeliveryOnSameChannel(
    selected: SubmissionDeliveryDto,
    deliveries: SubmissionDeliveryDto[]
): boolean {
    return deliveries.some(
        (d) => d.channel === selected.channel && isDeliveryInProgressState(d.currentStateType)
    );
}

export interface SubmissionDeliveryPanelProps {
    submission?: SubmissionDto;
}

export default function SubmissionDeliveryPanel(props: Readonly<SubmissionDeliveryPanelProps>) {
    const {submission} = props;

    const {submissionClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const roles = useUserRoles();
    const isAdministrator = UserRoleResolver.isAdministrator(roles);

    const mountedRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(timerRef.current);
        };
    }, []);

    const [submissionDeliveries, setSubmissionDeliveries] = useState<SubmissionDeliveryDto[]>();
    const [redeliveryEnabled, setRedeliveryEnabled] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState<SubmissionDeliveryDto | undefined>();
    const [redeliverDialogOpen, setRedeliverDialogOpen] = useState(false);
    const [isRedelivering, setIsRedelivering] = useState(false);

    const retryFinalizeDelivery = (submissionId: number, newDeliveryIds: number[], attemptsLeft = 5): Promise<void> => {
        return submissionClient.findSubmissionDeliveries(submissionId)
            .then((deliveryList) => {
                if (!mountedRef.current) return;

                setSubmissionDeliveries(deliveryList.items);
                setRedeliveryEnabled(!!deliveryList.acl?.canRedeliverSubmission);

                const outcome = getDeliveryPollOutcome(newDeliveryIds, deliveryList, attemptsLeft);

                if (outcome === 'done') {
                    const newIdSet = new Set(newDeliveryIds);
                    notifyRedeliveryOutcome(deliveryList.items.filter((d) => newIdSet.has(d.id)));
                    return;
                }

                if (outcome === 'exhausted' || outcome === 'exhausted-partial') {
                    throw new DeliveryTimeoutError('Redelivery did not finalize in time.');
                }

                return new Promise<void>((resolve, reject) => {
                    timerRef.current = setTimeout(() => {
                        if (!mountedRef.current) {
                            resolve();
                            return;
                        }
                        retryFinalizeDelivery(submissionId, newDeliveryIds, attemptsLeft - 1)
                            .then(resolve)
                            .catch(reject);
                    }, 2_000);
                });
            });
    };

    function getDeliveryPollOutcome(
        newDeliveryIds: number[],
        deliveryList: SubmissionDeliveryResultListDto,
        attemptsLeft: number
    ): 'done' | 'retry' | 'exhausted' | 'exhausted-partial' {
        const idSet = new Set(newDeliveryIds);
        const newDeliveries = deliveryList.items.filter((d) => idSet.has(d.id));

        if (newDeliveries.length > 0 && newDeliveries.every(isDeliveryFinalized)) {
            return 'done';
        }

        if (attemptsLeft <= 0) {
            return newDeliveries.length === 0 || newDeliveries.some(isDeliveryFinalized)
                ? 'exhausted-partial'
                : 'exhausted';
        }

        return 'retry';
    }

    function isDeliveryFinalized(delivery: SubmissionDeliveryDto): boolean {
        return delivery.currentStateType === SubmissionDeliveryStateType.DELIVERED ||
            delivery.currentStateType === SubmissionDeliveryStateType.DELIVERY_FAILED;
    }

    function notifyRedeliveryOutcome(newDeliveries: SubmissionDeliveryDto[]): void {
        const failed = newDeliveries.filter(
            (d) => d.currentStateType === SubmissionDeliveryStateType.DELIVERY_FAILED
        );
        if (failed.length === 0) {
            snackbar.success(t(SubmissionTranslKey.SUBMISSION_REDELIVER_REQUESTED));
        } else if (failed.length === newDeliveries.length) {
            snackbar.error(
                t(SubmissionTranslKey.SUBMISSION_REDELIVER_FAILED),
                new Error(`Redelivery failed for deliveries: ${failed.map((d) => d.id).join(', ')}`)
            );
        } else {
            snackbar.warning(t(SubmissionTranslKey.SUBMISSION_REDELIVER_PARTIALLY_FAILED));
        }
    }

    const runRedelivery = (
        submissionId: number,
        deliveries: Promise<SubmissionDeliveryDto[]>
    ) => {
        setIsRedelivering(true);
        deliveries
            .then((newDeliveries) => {
                if (!mountedRef.current) return;
                if (newDeliveries.every(isDeliveryFinalized)) {
                    const newIds = new Set(newDeliveries.map((d) => d.id));
                    setSubmissionDeliveries((prev) =>
                        [...(prev ?? []).filter((d) => !newIds.has(d.id)), ...newDeliveries]
                    );
                    notifyRedeliveryOutcome(newDeliveries);
                    return;
                }
                return retryFinalizeDelivery(submissionId, newDeliveries.map((d) => d.id));
            })
            .catch((err) => {
                if (!mountedRef.current) return;
                if (err instanceof DeliveryTimeoutError) {
                    snackbar.warning(t(SubmissionTranslKey.SUBMISSION_DELIVERY_STILL_IN_PROGRESS));
                } else {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
                }
            })
            .finally(() => {
                if (mountedRef.current) setIsRedelivering(false);
            });
    };

    const fetchSubmissionDeliveries = useCallback((submissionId: number, silent = false) => {
        return submissionClient.findSubmissionDeliveries(submissionId)
            .then((deliveryList) => {
                if (!mountedRef.current) return;
                setSubmissionDeliveries(deliveryList.items);
                setRedeliveryEnabled(!!deliveryList.acl?.canRedeliverSubmission);
            })
            .catch((error) => {
                if (!silent) snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
            });
    }, [submissionClient, snackbar, t]);

    useEffect(() => {
        if (submission) {
            setSelectedDelivery(undefined);
            fetchSubmissionDeliveries(submission.id);
        }
    }, [submission, fetchSubmissionDeliveries]);

    const hasInProgressDelivery = (submissionDeliveries ?? []).some(
        (d) => isDeliveryInProgressState(d.currentStateType)
    );

    useEffect(() => {
        if (!submission || !hasInProgressDelivery || isRedelivering) {
            return;
        }
        const intervalId = setInterval(() => {
            fetchSubmissionDeliveries(submission.id, true);
        }, AUTO_REFRESH_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [submission, hasInProgressDelivery, isRedelivering, fetchSubmissionDeliveries]);

    if (!submission || !submissionDeliveries) {
        return (
            <Skeleton count={2}/>
        );
    }

    const submissionId = submission.id;

    const liveSelectedDelivery = selectedDelivery
        ? submissionDeliveries.find((d) => d.id === selectedDelivery.id) ?? selectedDelivery
        : undefined;

    const isRedeliverDeliveryDisabled = !redeliveryEnabled
        || !liveSelectedDelivery
        || isRedelivering
        || hasInProgressDeliveryOnSameChannel(liveSelectedDelivery, submissionDeliveries);

    function handleConfirm() {
        setRedeliverDialogOpen(false);
        if (!liveSelectedDelivery) return;
        runRedelivery(submissionId, submissionClient.redeliverDelivery(submissionId, liveSelectedDelivery.id).then((d) => [d]));
    }

    return (
        <>
            <Stack spacing={2} padding={1}>
                <SubmissionDeliveryGrid
                    key={submission.id}
                    submissionDelivery={submissionDeliveries}
                    onRowSelected={setSelectedDelivery}
                />
                {isAdministrator && (
                    <Stack direction={"row"} justifyContent={"flex-end"} spacing={1} padding={1}>
                        <Button
                            data-testid={"redeliver-delivery-btn"}
                            variant={"primary"}
                            size={"m"}
                            onClick={() => setRedeliverDialogOpen(true)}
                            disabled={isRedeliverDeliveryDisabled}
                            inProgress={isRedelivering}
                        >
                            {t(SubmissionTranslKey.SUBMISSION_REDELIVER_DELIVERY_BTN)}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <ConfirmationDialog
                open={redeliverDialogOpen}
                title={t(SubmissionTranslKey.SUBMISSION_REDELIVER_DIALOG_TITLE)}
                message={t(SubmissionTranslKey.SUBMISSION_REDELIVER_DELIVERY_DIALOG_MSG)}
                onConfirmation={handleConfirm}
                onCancel={() => setRedeliverDialogOpen(false)}
            />
        </>
    );
}
